// 1 product's own rows (data.js), loaded once when its pages open and shared by every page and part
// inside it: the frame's menu, the recording page, Resources, the welcome checklist, the certificate
// and each product's own screens. A write updates the rows here as it goes, so a tick or a saved
// position shows on every page without loading again.
//
//   <ProductDataProvider productId={row.id} personId={me.person.id} type={row.type} slug={row.slug}>
//   const { status, data, reload, patch } = useProductData()
//   status: 'loading' | 'ready' | 'error'; data: loadProductData's answer; patch(fn) replaces data
//   with fn(data)
//   data.calendarAdded: a mastermind's calendar flag, set by either of its 2 Add to calendar buttons (the next
//   session's and the welcome checklist's), so the other says it's done too (the Accelerator critique's member 12,
//   20 Oct); kept for this visit only, never saved
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { loadProductData } from './data.js'

const ProductDataContext = createContext(null)

// type and slug: the product's, from the Library's catalogue row; a mastermind's and Profit On Your
// Terms's reads ask for a little more (data.js loadProductData)
export function ProductDataProvider({ productId, personId, type = null, slug = null, children }) {
  const [s, set] = useState({ status: 'loading', data: null, error: null })
  const live = useRef(0)

  const reload = useCallback(() => {
    const n = ++live.current
    set(prev => ({ status: prev.data ? 'ready' : 'loading', data: prev.data, error: null }))
    return loadProductData(productId, personId, { type, slug })
      .then(data => { if (n === live.current) set({ status: 'ready', data, error: null }) })
      .catch(error => { if (n === live.current) set(prev => (prev.data ? prev : { status: 'error', data: null, error })) })
  }, [productId, personId, type, slug])

  useEffect(() => {
    set({ status: 'loading', data: null, error: null })
    reload()
    return () => { live.current++ }
  }, [reload])

  const patch = useCallback(fn => set(prev => (prev.data ? { ...prev, data: fn(prev.data) } : prev)), [])

  return (
    <ProductDataContext.Provider value={{ ...s, reload, patch }}>
      {children}
    </ProductDataContext.Provider>
  )
}

export function useProductData() {
  return useContext(ProductDataContext)
}
